import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './AdjustedBill.module.css';
import BreakdownMaybe from '../../containers/TransactionPage/TransactionPanel/BreakdownMaybe';
import OrderBreakdown from '../OrderBreakdown/OrderBreakdown';
import AdjustedBillActivity from './AdjustedBillActivity';
import { useHistory } from 'react-router-dom';
import { LINE_ITEM_PROVIDER_COMMISSION, LINE_ITEM_CUSTOMER_COMMISSION } from '../../util/types';

// Action button that shows a loading state while its transition is dispatching
// (btnProps.inProgress comes from stateData: transitionInProgress === transitionName).
const ActionButton = ({ btnProps, className, label, onClick }) => {
    const inProgress = !!btnProps?.inProgress;
    const handleClick = onClick || (() => btnProps?.onAction && btnProps.onAction());
    return (
        <button type="button" className={className} onClick={handleClick} disabled={inProgress}>
            {inProgress ? 'Processing…' : label}
        </button>
    );
};

const { Money } = sdkTypes;

const AdjustedBill = props => {
    const intl = useIntl();
    const history = useHistory();
    const {
        isCustomer,
        isProvider,
        currentUser,
        transaction,
        listing,
        config,
        hasOvertimeRates,
        orderBreakdown,
        processName,
        stateData,
        note,
        priceVariantName,
        showBreakDown,
        isBiddingFlow,
        onSubmit,
        onOpenMakeCounterOfferModal,
        onOpenFinalCounterOfferModal,
        onOpenUpdateOfferModal,
    } = props;

    const {
        createdAt,
        protectedData: transactionProtectedData = {},
        payinTotal,
        lineItems = [],
        payoutTotal,
        lastTransition
    } = (transaction && transaction.id && transaction.attributes) || {};
    const now = new Date();
    // Get the difference in milliseconds
    const diff = now - createdAt;

    // Convert to seconds
    const diffSeconds = Math.floor(diff / 1000);

    const hours = Math.floor(diffSeconds / 3600);
    const minutes = Math.floor((diffSeconds % 3600) / 60);

    // get listing attributes
    const { attributes, author } = (listing && listing.id && listing) || {};
    const { title, publicData = {} } = (attributes) || {};
    const { eventDates = [] } = publicData;
    const { publicData: authorPublicData = {} } = (author && author.id && author.attributes?.profile) || {};
    const { companyName } = authorPublicData;
    const { services = [], finalBid, invoiceData = {} } = transactionProtectedData;
    const { additionalExpenses = [], documents = [], overtimeDetails = null, reductionDetails = null } = invoiceData;
    const invoiceType = invoiceData?.invoiceType || transactionProtectedData?.invoiceType || null;
    const hasAddtionalExpenses = !!(Array.isArray(additionalExpenses) && additionalExpenses.length > 0);
    const totalAdditionalExpenses = additionalExpenses.reduce((total, expense) => total + (expense?.cost?.amount || 0), 0);
    const hasDocuments = !!(Array.isArray(documents) && documents.length > 0);
    const isOvertimeInvoice =
        !!transactionProtectedData?.isOvertimeInvoice ||
        invoiceType === 'overtime' ||
        !!overtimeDetails?.totalOvertimeCost?.amount ||
        !!overtimeDetails?.overtimeRate?.amount ||
        (typeof overtimeDetails?.overtimeHours === 'string' && overtimeDetails.overtimeHours.trim().length > 0);
    const isReductionInvoice =
        !!transactionProtectedData?.isReductionInvoice ||
        invoiceType === 'reduction' ||
        !!reductionDetails;
    const isExpenseInvoice =
        !!transactionProtectedData?.isExpenseInvoice ||
        invoiceType === 'expense';
    // Any negotiation invoice (overtime / expense / reduction) — as opposed to a plain offer.
    const isInvoiceAdjustment = isOvertimeInvoice || isReductionInvoice || isExpenseInvoice;

    // Deduction reason code -> human label
    const DEDUCTION_REASON_LABELS = {
        service_not_rendered: 'Service Not Rendered',
        poor_quality: 'Poor Quality',
        late_arrival: 'Late Arrival',
        early_departure: 'Early Departure',
        equipment_failure: 'Equipment Failure',
        other: 'Other',
    };
    const getDeductionReasonLabel = code => DEDUCTION_REASON_LABELS[code] || code || 'Other';

    const overtimeCurrency = overtimeDetails?.totalOvertimeCost?.currency || overtimeDetails?.overtimeRate?.currency || config.currency;
    const overtimeTotalAmount = parseInt(overtimeDetails?.totalOvertimeCost?.amount) || 0;
    const overtimeTotalMoney = new Money(overtimeTotalAmount, overtimeCurrency);

    const overtimeRateAmount = parseInt(overtimeDetails?.overtimeRate?.amount) || 0;
    const overtimeRateMoney = new Money(overtimeRateAmount, overtimeCurrency);

    const overtimeHours = overtimeDetails?.overtimeHours;
    const overtimeReason = overtimeDetails?.overtimeReason;

    // Reduction (credit) details
    const reductionCurrency = reductionDetails?.totalReduction?.currency || config.currency;
    const reductionTotalAmount = parseInt(reductionDetails?.totalReduction?.amount) || 0;
    const reductionTotalMoney = new Money(reductionTotalAmount, reductionCurrency);
    const deductions = Array.isArray(reductionDetails?.deductions) ? reductionDetails.deductions : [];
    const hasDeductions = deductions.length > 0;
    const reductionReason = reductionDetails?.reductionReason;

    const hasMultipleEventDates = Array.isArray(eventDates) && eventDates.length > 1;

    // transition Checks
    const isMakeOffer = lastTransition === 'transition/make-offer';

    const isPaymentCompleted = lastTransition === 'transition/confirm-payment';
    const negotiationState = stateData?.processState;
    const offers = transaction?.attributes?.metadata?.offers;
    const latestOffer = Array.isArray(offers) && offers.length > 0 ? offers[offers.length - 1] : null;
    const hasFinalOffer = !!latestOffer?.isFinalOffer;
    const providerAcceptedCustomerCounterOffer =
        lastTransition === 'transition/provider-accept-counter-offer';

    const isLeaveReviewVisible = negotiationState === 'completed' ||
        negotiationState === 'reviewed-by-customer' ||
        negotiationState === 'reviewed-by-provider';

    const showLeaveReviewCta = isLeaveReviewVisible && stateData?.primaryButtonProps?.onAction;

    // Negotiation flow state flags
    const isAutoApproved = lastTransition === 'transition/auto-approve-offer' ||
        !!transaction?.attributes?.metadata?.offerAutoApproved;
    const isAfterReject = lastTransition === 'transition/provider-reject-counter-offer';
    const isAfterAutoReject = lastTransition === 'transition/auto-reject-counter-offer';

    // Invoice adjustments are tied to the event listing: the listing author is the
    // event owner (payer). Provider-submitted invoices (expense / overtime / credit)
    // must be accepted by the owner — not by the vendor who created them. Flex may
    // store the wrong customer/provider on initiate (make-offer is a customer actor),
    // so do not rely on URL role (/order vs /sale) for adjustment actions.
    // For invoice adjustments, the submitter is always the Sharetribe provider
    // (expert d) and the acceptor is always the Sharetribe customer (event owner/finding d).
    // This is because Sharetribe sets provider = expert and customer = event owner on invoice txs.
    // On invoice txs, Sharetribe provider = expert (submitter), customer = event owner (acceptor).
    const showCustomerOfferPendingActions = isCustomer;
    const showProviderOfferPendingActions = isProvider;

    const canMakeCounterOffer = negotiationState === 'offer-pending' && showCustomerOfferPendingActions &&
        !isAutoApproved && !hasFinalOffer && !isAfterReject && !isAfterAutoReject &&
        !providerAcceptedCustomerCounterOffer;

    // Custom breakdown logic for negotiation invoices (overtime / expense / reduction)
    const initialSubtotal = totalAdditionalExpenses + overtimeTotalAmount - reductionTotalAmount;
    const negotiatedSubtotalLineItem = lineItems.find(
        item => item.code === 'line-item/invoice-amount' && !item.reversal
    );
    const negotiatedSubtotal = isReductionInvoice
        ? -reductionTotalAmount
        : (negotiatedSubtotalLineItem?.lineTotal?.amount || initialSubtotal);
    const adjustmentAmount = negotiatedSubtotal - initialSubtotal;

    const providerCommissionLineItem = lineItems.find(
        item => item.code === LINE_ITEM_PROVIDER_COMMISSION && !item.reversal
    );
    const customerCommissionLineItem = lineItems.find(
        item => item.code === LINE_ITEM_CUSTOMER_COMMISSION && !item.reversal
    );

    const commissionAmount = isProvider
        ? (providerCommissionLineItem?.lineTotal?.amount || 0)
        : (customerCommissionLineItem?.lineTotal?.amount || 0);

    const finalTotalAmount = negotiatedSubtotal + commissionAmount;

    const overtimeBreakdownLineItems = [
        {
            code: 'line-item/Additional-Service-Amount',
            unitPrice: new Money(totalAdditionalExpenses, config.currency),
            quantity: 1,
            lineTotal: new Money(totalAdditionalExpenses, config.currency),
            includeFor: ['customer', 'provider'],
        },
        {
            code: 'line-item/Overtime-Amount',
            unitPrice: overtimeTotalMoney,
            quantity: 1,
            lineTotal: overtimeTotalMoney,
            includeFor: ['customer', 'provider'],
        },
        reductionTotalAmount > 0 ? {
            code: 'line-item/Bill-Reduction',
            unitPrice: new Money(-reductionTotalAmount, reductionCurrency),
            quantity: 1,
            lineTotal: new Money(-reductionTotalAmount, reductionCurrency),
            includeFor: ['customer', 'provider'],
        } : null,
        adjustmentAmount !== 0 ? {
            code: 'line-item/Negotiated-Adjustment',
            unitPrice: new Money(adjustmentAmount, config.currency),
            quantity: 1,
            lineTotal: new Money(adjustmentAmount, config.currency),
            includeFor: ['customer', 'provider'],
        } : null,
        providerCommissionLineItem,
        customerCommissionLineItem,
    ].filter(Boolean);

    // Mock transaction for OrderBreakdown components to reuse their logic and styling
    const mockTransaction = {
        ...transaction,
        attributes: {
            ...transaction.attributes,
            lineItems: overtimeBreakdownLineItems,
            payinTotal: new Money(
                negotiatedSubtotal + (customerCommissionLineItem ? customerCommissionLineItem.lineTotal.amount : 0),
                config.currency
            ),
            payoutTotal: new Money(
                negotiatedSubtotal + (providerCommissionLineItem ? providerCommissionLineItem.lineTotal.amount : 0),
                config.currency
            ),
        }
    };

    return (
        <div className={css.billReductionPage}>
            <div className={css.breadcrumb}>
                <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        history.push(isCustomer ? '/dashboard/owner?tab=event' : '/dashboard/bidder?tab=event');
                    }}
                >
                    <FormattedMessage id="AdjustedBillPage.breadcrumbDashboard" />
                </span>
                <span className={css.breadcrumbSeparator}>/</span>
                <span className={css.breadcrumbCurrent}>
                    {isCustomer ? <FormattedMessage id="AdjustedBillPage.breadcrumbCurrentCustomer" /> : <FormattedMessage id="AdjustedBillPage.breadcrumbCurrentProvider" />}
                </span>
            </div>

            <div className={css.header}>
                <div className={css.headerContent}>
                    <h1 className={css.title}>
                        {isReductionInvoice ? (
                            <FormattedMessage id="AdjustedBillPage.titleReduction" defaultMessage="Bill Reduction" />
                        ) : isExpenseInvoice ? (
                            <FormattedMessage id="AdjustedBillPage.titleExpense" defaultMessage="Expense Coverage" />
                        ) : isCustomer ? (
                            <FormattedMessage id="AdjustedBillPage.titleCustomer" />
                        ) : (
                            <FormattedMessage id="AdjustedBillPage.titleProvider" />
                        )}
                    </h1>
                    <p className={css.subtitle}>
                        {isCustomer ? <FormattedMessage id="AdjustedBillPage.subtitleCustomer" /> : <FormattedMessage id="AdjustedBillPage.subtitleProvider" />}
                    </p>
                </div>
                <span className={css.statusPill}>
                    {negotiationState === "reviewed" ? "Reviewed" : <FormattedMessage id="AdjustedBillPage.statusPill" />}
                </span>
            </div>

            {hasOvertimeRates ? <div className={css.alert}>
                <div className={css.alertLeft}>
                    <span className={css.alertIcon}>!</span>
                    <div className={css.alertText}>
                        <p className={css.alertTitle}>
                            <FormattedMessage id="AdjustedBillPage.alertTitle" />
                        </p>
                        <p className={css.alertSubtitle}>
                            <FormattedMessage id="AdjustedBillPage.alertSubtitle" />
                        </p>
                    </div>
                </div>
                <div className={css.alertRight}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            d="M10.0013 1.6665C14.6038 1.6665 18.3346 5.39734 18.3346 9.99984C18.3346 14.6023 14.6038 18.3332 10.0013 18.3332C5.3988 18.3332 1.66797 14.6023 1.66797 9.99984C1.66797 5.39734 5.3988 1.6665 10.0013 1.6665ZM10.0013 4.99984C9.78029 4.99984 9.56833 5.08764 9.41205 5.24392C9.25577 5.4002 9.16797 5.61216 9.16797 5.83317V9.99984C9.16802 10.2208 9.25584 10.4328 9.41214 10.589L11.9121 13.089C12.0693 13.2408 12.2798 13.3248 12.4983 13.3229C12.7168 13.321 12.9258 13.2334 13.0803 13.0789C13.2348 12.9243 13.3225 12.7153 13.3244 12.4968C13.3263 12.2783 13.2423 12.0678 13.0905 11.9107L10.8346 9.65484V5.83317C10.8346 5.61216 10.7468 5.4002 10.5906 5.24392C10.4343 5.08764 10.2223 4.99984 10.0013 4.99984Z"
                            fill="#E69A2A"
                        />
                    </svg>
                    <span className={css.alertTime}>
                        <FormattedMessage id="AdjustedBillPage.alertTime" />
                        {hours > 0 ? `${hours} hours,` : null}
                        {minutes > 0 ? `${minutes} mins` : null} ago
                    </span>
                </div>
            </div> : null}

            <div className={css.card}>
                <h3 className={css.cardTitle}>
                    {title}
                </h3>
                <div className={css.infoRow}>
                    <div className={css.infoItem}>
                        <span className={css.infoLabel}>
                            <FormattedMessage id="AdjustedBillPage.eventIdLabel" />
                        </span>
                        <span className={css.infoValue}>
                            {listing?.id?.uuid}
                        </span>
                    </div>
                    <div className={css.infoItem}>
                        <span className={css.infoLabel}>
                            <FormattedMessage id="AdjustedBillPage.eventDateLabel" />
                        </span>
                        <span className={css.infoValue}>
                            {eventDates[0]} {hasMultipleEventDates ? ` - ${eventDates[eventDates.length - 1]}` : null}
                        </span>
                    </div>
                    <div className={css.infoItem}>
                        <span className={css.infoLabel}>
                            <FormattedMessage id="AdjustedBillPage.companyLabel" />
                        </span>
                        <span className={css.infoValue}>
                            {companyName}
                        </span>
                    </div>
                    {hasOvertimeRates ? <div className={css.infoItem}>
                        <span className={css.infoLabel}>
                            <FormattedMessage id="AdjustedBillPage.overtimeRateLabel" />
                        </span>
                        <span className={css.infoValue}>
                            <FormattedMessage id="AdjustedBillPage.overtimeRateValue" />
                        </span>
                    </div> : null}
                </div>
                <div className={css.horizontalLine} />
                <div className={css.infoRow}>
                    <div className={css.infoItem}>
                        <span className={css.infoLabel}>
                            <FormattedMessage id="AdjustedBillPage.eventTimelineLabel" />
                        </span>
                    </div>
                </div>
            </div>

            <div className={css.card}>
                <h3 className={css.cardTitle}>
                    <FormattedMessage id="AdjustedBillPage.breakdownTitle" />
                </h3>

                {/* base pricing removed as per requirements */}

                {/* additional pricing */}
                {hasAddtionalExpenses ? (
                    <div className={css.breakdownSection}>
                        <div className={css.breakdownHeader}>
                            <div>
                                <p className={css.breakdownHeading}>
                                    <FormattedMessage id="AdjustedBillPage.additionalExpensesTitle" />
                                </p>
                                <p className={css.breakdownSubheading}>
                                    <FormattedMessage id="AdjustedBillPage.additionalExpensesSubtitle" />
                                </p>
                            </div>
                            <p className={css.breakdownAmount}>
                                {formatMoney(intl, new Money(totalAdditionalExpenses, config.currency))}
                            </p>
                        </div>
                        <div className={css.breakdownBox}>
                            <p className={css.breakdownBoxTitle}>
                                <FormattedMessage id="AdjustedBillPage.addtionalExpensesBoxTitle" />
                            </p>
                            {Array.isArray(additionalExpenses) && additionalExpenses.length
                                ? additionalExpenses.map((expense, index) => {
                                    const category = expense.category;
                                    const amount = expense?.cost?.amount;
                                    const formattedAmount = formatMoney(intl, new Money(amount, config.currency));
                                    const quantity = expense.quantity;

                                    return (
                                        <div key={index} className={css.breakdownRow}>
                                            <span>{category}</span>
                                            <span>{quantity}</span>
                                            <span>{formattedAmount}</span>
                                        </div>
                                    );
                                })
                                : null}
                        </div>
                    </div>
                ) : null}

                {/* overtime pricing */}
                {isOvertimeInvoice ? (
                    <div className={css.breakdownSection}>
                        <div className={css.breakdownHeader}>
                            <div>
                                <p className={css.breakdownHeading}>
                                    <FormattedMessage id="AdjustedBillPage.overtimeTitle" />
                                </p>
                                <p className={css.breakdownSubheading}>
                                    <FormattedMessage id="AdjustedBillPage.overtimeSubtitle" />
                                </p>
                            </div>
                            <p className={css.breakdownAmount}>{formatMoney(intl, overtimeTotalMoney)}</p>
                        </div>
                        <div className={css.breakdownBox}>
                            <p className={css.breakdownBoxTitle}>
                                <FormattedMessage id="AdjustedBillPage.overtimeBoxTitle" />
                            </p>
                            <div className={css.breakdownRow}>
                                <b>
                                    <FormattedMessage id="AdjustedBillPage.overtimeScheduledLabel" />
                                </b>
                                <span>
                                    {typeof overtimeHours === 'string' && overtimeHours.trim().length > 0
                                        ? overtimeHours
                                        : '-'}
                                </span>
                            </div>
                            <div className={css.breakdownRow}>
                                <b>
                                    <FormattedMessage id="AdjustedBillPage.overtimeActualLabel" />
                                </b>
                                <span>{formatMoney(intl, overtimeRateMoney)}</span>
                            </div>
                            <div className={css.breakdownRow}>
                                <b>
                                    <FormattedMessage id="AdjustedBillPage.overtimeTotalLabel" />
                                </b>
                                <span>{formatMoney(intl, overtimeTotalMoney)}</span>
                            </div>
                        </div>
                    </div>
                ) : null}

                {isOvertimeInvoice ? (
                    <div className={css.reasonBoxNeutral}>
                        <p className={css.reasonTitleNeutral}>
                            <FormattedMessage id="AdjustedBillPage.reasonTitle" />
                        </p>
                        <p className={css.reasonTextNeutral}>
                            {typeof overtimeReason === 'string' && overtimeReason.trim().length > 0
                                ? overtimeReason
                                : intl.formatMessage({ id: 'AdjustedBillPage.reasonText' })}
                        </p>
                        <p className={css.reasonMetaNeutral}>
                            <FormattedMessage id="AdjustedBillPage.reasonMeta" />
                        </p>
                    </div>
                ) : null}

                {/* bill reduction (credit) */}
                {isReductionInvoice ? (
                    <div className={css.breakdownSection}>
                        <div className={css.breakdownHeader}>
                            <div>
                                <p className={css.breakdownHeading}>
                                    <FormattedMessage id="AdjustedBillPage.reductionTitle" defaultMessage="Bill Reduction" />
                                </p>
                                <p className={css.breakdownSubheading}>
                                    <FormattedMessage id="AdjustedBillPage.reductionSubtitle" defaultMessage="Deductions applied as a credit to this bill" />
                                </p>
                            </div>
                            <p className={css.breakdownAmount}>
                                {reductionTotalAmount > 0 ? `-${formatMoney(intl, reductionTotalMoney)}` : formatMoney(intl, reductionTotalMoney)}
                            </p>
                        </div>
                        <div className={css.breakdownBox}>
                            <p className={css.breakdownBoxTitle}>
                                <FormattedMessage id="AdjustedBillPage.reductionBoxTitle" defaultMessage="Deduction Details" />
                            </p>
                            {hasDeductions
                                ? deductions.map((deduction, index) => {
                                    const amount = parseInt(deduction?.amount?.amount) || 0;
                                    const currency = deduction?.amount?.currency || reductionCurrency;
                                    const formattedAmount = formatMoney(intl, new Money(amount, currency));
                                    return (
                                        <div key={deduction?.id || index} className={css.breakdownRow}>
                                            <span>{getDeductionReasonLabel(deduction?.reason)}</span>
                                            <span>{amount > 0 ? `-${formattedAmount}` : formattedAmount}</span>
                                        </div>
                                    );
                                })
                                : null}
                            <div className={css.breakdownRow}>
                                <b>
                                    <FormattedMessage id="AdjustedBillPage.reductionTotalLabel" defaultMessage="Total Reduction" />
                                </b>
                                <span>
                                    {reductionTotalAmount > 0 ? `-${formatMoney(intl, reductionTotalMoney)}` : formatMoney(intl, reductionTotalMoney)}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : null}

                {isReductionInvoice && typeof reductionReason === 'string' && reductionReason.trim().length > 0 ? (
                    <div className={css.reasonBoxNeutral}>
                        <p className={css.reasonTitleNeutral}>
                            <FormattedMessage id="AdjustedBillPage.reductionReasonTitle" defaultMessage="Reason for Reduction" />
                        </p>
                        <p className={css.reasonTextNeutral}>{reductionReason}</p>
                    </div>
                ) : null}
            </div>

            {hasDocuments ? (
                <div className={css.card}>
                    <h3 className={css.cardTitle}>
                        <FormattedMessage id="AdjustedBillPage.supportingDocumentsTitle" />
                    </h3>
                    <div className={css.documentList}>
                        {documents.map((document, index) => {
                            const fileName = document.fileName;
                            const URL = document.url;

                            return (
                                <div key={index} className={css.documentRow}>
                                    <div className={css.documentLeft}>
                                        <span className={css.documentIcon} aria-hidden="true">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="25"
                                                height="25"
                                                viewBox="0 0 25 25"
                                                fill="none"
                                            >
                                                <path
                                                    d="M5.20833 21.875C4.63542 21.875 4.14514 21.6712 3.7375 21.2635C3.32986 20.8559 3.12569 20.3653 3.125 19.7917V5.20833C3.125 4.63542 3.32917 4.14514 3.7375 3.7375C4.14583 3.32986 4.63611 3.12569 5.20833 3.125H19.7917C20.3646 3.125 20.8552 3.32917 21.2635 3.7375C21.6719 4.14583 21.8757 4.63611 21.875 5.20833V19.7917C21.875 20.3646 21.6712 20.8552 21.2635 21.2635C20.8559 21.6719 20.3653 21.8757 19.7917 21.875H5.20833ZM6.25 17.7083H18.75L14.8438 12.5L11.7188 16.6667L9.375 13.5417L6.25 17.7083Z"
                                                    fill="#6939CB"
                                                />
                                            </svg>
                                        </span>
                                        <div className={css.documentText}>
                                            <p className={css.documentName}>{fileName}</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className={css.documentView}
                                        onClick={() => window.open(URL, '_blank')}
                                    >
                                        <FormattedMessage id="AdjustedBillPage.documentView" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            <div className={css.card}>
                <h3 className={css.cardTitle}>
                    <FormattedMessage id="AdjustedBillPage.paymentDetailsTitle" />
                </h3>

            </div>

            <div className={css.card}>
                <div className={css.totalRow}>
                    <div>
                        <h3 className={css.totalTitle}>
                            <FormattedMessage id="AdjustedBillPage.totalTitle" />
                        </h3>
                        {isInvoiceAdjustment ? (
                            <p className={css.totalSubtitle}>
                                <FormattedMessage id="AdjustedBillPage.totalSubtitle" />
                            </p>
                        ) : null}
                    </div>
                    <div className={css.totalAmount}>
                        {formatMoney(intl, new Money(negotiatedSubtotal, config.currency))}
                    </div>
                </div>

                {isInvoiceAdjustment ? (
                    <BreakdownMaybe
                        className={css.breakdownContainer}
                        orderBreakdown={
                            <OrderBreakdown
                                transaction={mockTransaction}
                                userRole={isProvider ? 'provider' : 'customer'}
                                marketplaceName={config.marketplaceName}
                                config={config}
                            />
                        }
                        processName={processName}
                        intl={intl}
                        note={note}
                        priceVariantName={priceVariantName}
                    />
                ) : (
                    showBreakDown && !isBiddingFlow ? (
                        <BreakdownMaybe
                            className={css.breakdownContainer}
                            orderBreakdown={orderBreakdown}
                            processName={processName}
                            intl={intl}
                            note={note}
                            priceVariantName={priceVariantName}
                        />
                    ) : null
                )}

                {/* Negotiation Action Buttons */}
                <div className={css.totalButtons}>
                    {/* ===== CUSTOMER in offer-pending ===== */}
                    {negotiationState === 'offer-pending' && showCustomerOfferPendingActions ? (
                        <>
                            {/* Contextual info messages */}
                            {isAutoApproved ? (
                                <div className={css.negotiationMessage}>
                                    Offer has been auto-approved. Please proceed with payment.
                                </div>
                            ) : null}

                            {providerAcceptedCustomerCounterOffer ? (
                                <div className={css.counterOfferAccepted}>
                                    Your counter offer was accepted.
                                </div>
                            ) : null}

                            {hasFinalOffer ? (
                                <div className={css.negotiationMessage}>
                                    Provider sent a final offer.
                                </div>
                            ) : null}

                            {isAfterReject ? (
                                <div className={css.negotiationMessage}>
                                    Counter offer was rejected. Original amount applies.
                                </div>
                            ) : null}

                            {/* Negotiate / counter offer button */}
                            {stateData?.tertiaryButtonProps?.onAction ? (
                                <ActionButton
                                    btnProps={stateData.tertiaryButtonProps}
                                    className={`${css.actionButton} ${css.negotiateButton || css.disputeButton}`}
                                    label={stateData.tertiaryButtonProps.buttonText || 'Negotiate'}
                                />
                            ) : null}

                            {/* Accept button. A reduction is a credit: accept it without
                                paying (non-charging accept-reduction via stateData). Any other
                                invoice/offer goes through payment (onSubmit -> checkout). */}
                            {isReductionInvoice ? (
                                <ActionButton
                                    btnProps={stateData?.primaryButtonProps}
                                    className={`${css.actionButton} ${css.acceptButton}`}
                                    label="Accept Credit"
                                />
                            ) : (
                                <button
                                    type="button"
                                    className={`${css.actionButton} ${css.acceptButton}`}
                                    onClick={onSubmit}
                                >
                                    Make Payment
                                </button>
                            )}

                            {/* Decline the adjustment: original booking amount stands, no adjustment applied */}
                            {stateData?.secondaryButtonProps?.onAction ? (
                                <ActionButton
                                    btnProps={stateData.secondaryButtonProps}
                                    className={`${css.actionButton} ${css.rejectButton || css.disputeButton}`}
                                    label={isInvoiceAdjustment ? 'Decline — Keep Original Amount' : 'Decline Offer'}
                                />
                            ) : null}
                        </>
                    ) : null}

                    {/* ===== PROVIDER in offer-pending ===== */}
                    {negotiationState === 'offer-pending' && showProviderOfferPendingActions ? (
                        <>
                            <div className={css.negotiationMessage}>
                                Waiting for customer's response.
                            </div>
                            {/* Withdraw the adjustment invoice while it is still pending */}
                            {stateData?.secondaryButtonProps?.onAction ? (
                                <ActionButton
                                    btnProps={stateData.secondaryButtonProps}
                                    className={`${css.actionButton} ${css.rejectButton || css.disputeButton}`}
                                    label={isInvoiceAdjustment ? 'Withdraw Invoice' : 'Withdraw Offer'}
                                />
                            ) : null}
                        </>
                    ) : null}

                    {/* ===== PROVIDER in customer-offer-pending ===== */}
                    {negotiationState === 'customer-offer-pending' && isProvider ? (
                        <>
                            {/* Accept counter offer */}
                            <ActionButton
                                btnProps={stateData?.primaryButtonProps}
                                className={`${css.actionButton} ${css.acceptButton}`}
                                label="Accept Counter Offer"
                            />

                            {/* Add Final Offer */}
                            <button
                                type="button"
                                className={`${css.actionButton} ${css.disputeButton}`}
                                onClick={() => {
                                    if (onOpenFinalCounterOfferModal) {
                                        onOpenFinalCounterOfferModal();
                                    }
                                }}
                            >
                                Add Final Offer
                            </button>

                            {/* Reject Counter Offer */}
                            <ActionButton
                                btnProps={stateData?.secondaryButtonProps}
                                className={`${css.actionButton} ${css.rejectButton || css.disputeButton}`}
                                label="Reject Counter Offer"
                            />
                        </>
                    ) : null}

                    {/* ===== CUSTOMER in customer-offer-pending (info only) ===== */}
                    {negotiationState === 'customer-offer-pending' && isCustomer ? (
                        <div className={css.negotiationMessage}>
                            Waiting for provider's response to your counter offer.
                        </div>
                    ) : null}

                    {/* ===== UPDATE_PENDING: owner countered a reduction — vendor must respond ===== */}
                    {negotiationState === 'update-pending' && showProviderOfferPendingActions ? (
                        <div className={css.negotiationMessage}>
                            You sent a counter offer. Waiting for the vendor to respond.
                            {stateData?.secondaryButtonProps?.onAction ? (
                                <ActionButton
                                    btnProps={stateData.secondaryButtonProps}
                                    className={`${css.actionButton} ${css.disputeButton}`}
                                    label="Withdraw Counter"
                                />
                            ) : null}
                        </div>
                    ) : null}

                    {negotiationState === 'update-pending' && showCustomerOfferPendingActions ? (
                        <>
                            <div className={css.negotiationMessage}>
                                The owner proposed a different reduction amount. Accept or reject below.
                            </div>
                            {stateData?.primaryButtonProps?.onAction ? (
                                <ActionButton
                                    btnProps={stateData.primaryButtonProps}
                                    className={`${css.actionButton} ${css.acceptButton}`}
                                    label="Accept Counter Offer"
                                />
                            ) : null}
                            {stateData?.secondaryButtonProps?.onAction ? (
                                <ActionButton
                                    btnProps={stateData.secondaryButtonProps}
                                    className={`${css.actionButton} ${css.rejectButton || css.disputeButton}`}
                                    label="Reject Counter Offer"
                                />
                            ) : null}
                        </>
                    ) : null}
                </div>

                <AdjustedBillActivity
                    transaction={transaction}
                    isOvertimeInvoice={isOvertimeInvoice}
                    invoiceType={isReductionInvoice ? 'reduction' : isExpenseInvoice ? 'expense' : isOvertimeInvoice ? 'overtime' : null}
                />

                {showLeaveReviewCta ? (
                    <div className={css.reviewSection}>
                        <div className={css.reviewSectionLeft}>
                            <div className={css.reviewSectionTitle}>
                                {isCustomer ? 'Leave Provider Review' : 'Leave Client Review'}
                            </div>
                            <div className={css.reviewSectionSubtitle}>
                                {isCustomer
                                    ? 'Share your experience working with the provider'
                                    : 'Share your experience working with the client'}
                            </div>
                        </div>
                        <button
                            type="button"
                            className={`${css.actionButton} ${css.acceptButton}`}
                            onClick={() => {
                                stateData.primaryButtonProps.onAction();
                            }}
                        >
                            Leave Review
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default AdjustedBill;